import { useState } from 'react';
import Modal from '../components/Modal.jsx';
import { useConfirm } from '../components/ConfirmDialog.jsx';
import { fmtCents, parseAmountToCents } from '../format.js';

export default function Goals({ state, generic, showToast, onBack }) {
  const [editing, setEditing] = useState(null);
  const visible = state.goals.filter(g => !g.archived).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  return (
    <div className="screen">
      <div className="row" style={{ marginBottom: 16, gap: 12 }}>
        <button onClick={onBack} className="ghost">‹</button>
        <h1 style={{ margin: 0 }}>Goals</h1>
      </div>
      <div className="row between" style={{ marginBottom: 12 }}>
        <span className="muted tiny">{visible.length} goals</span>
        <button className="primary" onClick={() => setEditing('new')}>+ New</button>
      </div>

      <div className="stack" style={{ gap: 8 }}>
        {visible.length === 0 ? (
          <div className="card empty"><div className="icon">∅</div><div>No goals yet</div></div>
        ) : visible.map(g => {
          const balance = g.live_balance_cents ?? g.current_cents;
          const pct = g.target_cents ? Math.min(100, (balance / g.target_cents) * 100) : 0;
          return (
            <button key={g.id} onClick={() => setEditing(g)} className="card"
                    style={{ textAlign: 'left', display: 'block', cursor: 'pointer', marginBottom: 0 }}>
              <div className="row between" style={{ marginBottom: 6 }}>
                <strong>
                  {g.name}
                  {g.account_id && <span className="pill info" style={{ marginLeft: 8 }}>Live</span>}
                </strong>
                <span className="muted tiny">{pct.toFixed(1)}%</span>
              </div>
              <div className="row between tiny muted" style={{ marginBottom: 8 }}>
                <span>{fmtCents(balance)} / {fmtCents(g.target_cents)}</span>
                {g.monthly_contribution_cents > 0 && <span>{fmtCents(g.monthly_contribution_cents)}/mo</span>}
              </div>
              <div style={{ background: 'var(--bg2)', height: 6, borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: 'var(--blue)' }} />
              </div>
            </button>
          );
        })}
      </div>

      <GoalEditor editing={editing} onClose={() => setEditing(null)}
                  state={state} generic={generic} showToast={showToast} />
    </div>
  );
}

function GoalEditor({ editing, onClose, state, generic, showToast }) {
  if (!editing) return null;
  const isNew = editing === 'new';
  const initial = isNew ? {
    name: '', target_cents: 0, current_cents: 0, monthly_contribution_cents: 0, notes: '',
  } : editing;

  return (
    <Modal open={!!editing} onClose={onClose} title={isNew ? 'New Goal' : 'Edit Goal'}>
      <GoalForm key={isNew ? 'new' : editing.id} initial={initial} isNew={isNew}
                state={state} generic={generic} showToast={showToast} onClose={onClose} />
    </Modal>
  );
}

function GoalForm({ initial, isNew, state, generic, showToast, onClose }) {
  const ask = useConfirm();
  const [form, setForm] = useState({
    name: initial.name ?? '',
    targetInput: initial.target_cents ? (initial.target_cents / 100).toFixed(2) : '',
    currentInput: initial.current_cents ? (initial.current_cents / 100).toFixed(2) : '',
    monthlyInput: initial.monthly_contribution_cents ? (initial.monthly_contribution_cents / 100).toFixed(2) : '',
    notes: initial.notes ?? '',
    dashboard_visible: initial.dashboard_visible ?? 1,
    account_id: initial.account_id ?? '',
  });
  const [saving, setSaving] = useState(false);
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function save() {
    if (!form.name) return showToast({ kind: 'error', message: 'Name required.' });
    const target = parseAmountToCents(form.targetInput);
    if (!target) return showToast({ kind: 'error', message: 'Target required.' });
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        target_cents: target,
        current_cents: parseAmountToCents(form.currentInput) ?? 0,
        monthly_contribution_cents: parseAmountToCents(form.monthlyInput) ?? 0,
        notes: form.notes,
        dashboard_visible: form.dashboard_visible ? 1 : 0,
        account_id: form.account_id || null,
      };
      if (isNew) await generic.goals.create(payload);
      else await generic.goals.update(initial.id, payload);
      showToast({ kind: 'success', message: 'Saved.' });
      onClose();
    } catch (e) { showToast({ kind: 'error', message: 'Failed: ' + e.message }); }
    finally { setSaving(false); }
  }

  async function archive() {
    const ok = await ask({ title: 'Archive goal?', message: 'You can restore it later from Settings.', confirmLabel: 'Archive', danger: true }); if (!ok) return;
    setSaving(true);
    try {
      await generic.goals.remove(initial.id);
      showToast({ kind: 'success', message: 'Archived.' });
      onClose();
    } catch (e) { showToast({ kind: 'error', message: 'Failed: ' + e.message }); }
    finally { setSaving(false); }
  }

  return (
    <>
      <div className="field"><label>Name</label>
        <input value={form.name} onChange={(e) => update('name', e.target.value)} autoFocus /></div>
      <div className="field">
        <label>Linked Account (optional)</label>
        <select value={form.account_id} onChange={(e) => update('account_id', e.target.value)}>
          <option value="">— Manual (no live updates) —</option>
          {state.accounts.filter(a => !a.archived).map(a => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
        <div className="tiny muted" style={{ marginTop: 4 }}>
          When linked, balance = baseline + transactions to this account.
        </div>
      </div>
      <div className="row" style={{ gap: 8 }}>
        <div className="field" style={{ flex: 1 }}><label>Target</label>
          <input inputMode="decimal" value={form.targetInput} onChange={(e) => update('targetInput', e.target.value)} /></div>
        <div className="field" style={{ flex: 1 }}>
          <label>{form.account_id ? 'Baseline' : 'Current'}</label>
          <input inputMode="decimal" value={form.currentInput} onChange={(e) => update('currentInput', e.target.value)} />
        </div>
      </div>
      <div className="field"><label>Monthly Contribution</label>
        <input inputMode="decimal" value={form.monthlyInput} onChange={(e) => update('monthlyInput', e.target.value)} /></div>
      <div className="field"><label>Notes</label>
        <input value={form.notes} onChange={(e) => update('notes', e.target.value)} /></div>
      <div className="field">
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, textTransform: 'none' }}>
          <input type="checkbox" checked={!!form.dashboard_visible}
                 onChange={(e) => update('dashboard_visible', e.target.checked ? 1 : 0)}
                 style={{ width: 'auto' }} />
          <span>Show on Dashboard</span>
        </label>
      </div>
      <div className="row between" style={{ marginTop: 24 }}>
        {!isNew ? <button className="danger" onClick={archive} disabled={saving}>Archive</button> : <span />}
        <div className="row" style={{ gap: 8 }}>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={save} disabled={saving}>{saving ? <span className="spinner"/> : 'Save'}</button>
        </div>
      </div>
    </>
  );
}
